import { ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Metadata } from 'next';

/**
 * Combines class names dynamically with Tailwind merge.
 *
 * @param inputs - The class names to combine
 * @returns A single string with combined class names
 */
export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

// Native debounce utility
export function debounce<T extends (...args: any[]) => void>(func: T, wait: number): (...args: Parameters<T>) => void {
	let timeout: ReturnType<typeof setTimeout> | null;

	return (...args: Parameters<T>) => {
		if (timeout) clearTimeout(timeout);
		timeout = setTimeout(() => func(...args), wait);
	};
}

/**
 * Parses simple markdown for bold (**text**) and italic (*text*) in headings.
 * Returns a React Node array.
 */
import React from 'react';

export function parseHeading(text: string | undefined | null): React.ReactNode {
	if (!text) return null;

	// Split by bold (**...**) and italic (*...*) markers
	// Regex captures the delimiters to preserve structure for mapping
	const parts = text.split(/(\*\*.*?\*\*|\*.*?\*)/g);

	return parts.map((part, index) => {
		if (part.startsWith('**') && part.endsWith('**')) {
			return (
				<strong key={index} className="font-bold">
					{' '}
					{part.slice(2, -2)}{' '}
				</strong>
			);
		}
		if (part.startsWith('*') && part.endsWith('*')) {
			return (
				<em key={index} className="italic">
					{' '}
					{part.slice(1, -1)}{' '}
				</em>
			);
		}

		return part;
	});
}

/**
 * Sanitizes and upgrades insecure content like standard HTTP youtube embeds to HTTPS.
 */
export function sanitizeHtml(html: string | undefined | null): string {
    if (!html) return '';

    return html.replace(/http:\/\/(www\.)?youtube\.com/g, 'https://$1youtube.com');
}
